import React, { useMemo, useState, useRef } from 'react';
import { Calendar } from 'react-date-range';
import { Popper } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { enUS } from 'date-fns/locale';
import { formatSeconds, Images } from 'utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
    SEGMENT_TYPE,
    isRunningSegment,
    segmentDurationMs,
    startPickerBounds,
    endPickerBounds,
} from './editSessionTimeline.helpers';

const customLocale = {
    ...enUS,
    options: {
        ...enUS.options,
        weekStartsOn: 0,
    },
};

const pad = (n) => String(n).padStart(2, '0');

const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const MM = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const yyyy = date.getFullYear();
    const HH = pad(date.getHours());
    const mm = pad(date.getMinutes());
    const ss = pad(date.getSeconds());
    return `${MM}/${dd}/${yyyy}, ${HH}:${mm}:${ss}`;
};

/** A single‑datetime picker styled like DateTypeSelect but with time (HH:mm:ss). */
const DateTimeInput = ({ value, onChange, minDate, maxDate }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const popperId = open ? 'est-datetime-popper' : undefined;

    const hourRef = useRef();
    const minuteRef = useRef();
    const secondRef = useRef();

    // Memoize so the Calendar receives a stable reference and doesn't
    // reset its displayed month on every parent re-render (1-second ticker).
    const dateObj = useMemo(() => (value ? new Date(value) : null), [value]);
    const calendarMinDate = useMemo(() => (minDate ? new Date(minDate) : undefined), [minDate]);
    const calendarMaxDate = useMemo(() => (maxDate ? new Date(maxDate) : undefined), [maxDate]);
    const hour = dateObj ? pad(dateObj.getHours()) : '';
    const minute = dateObj ? pad(dateObj.getMinutes()) : '';
    const second = dateObj ? pad(dateObj.getSeconds()) : '';

    const handleCalendarChange = (newDay) => {
        const base = dateObj || new Date();
        const next = new Date(newDay);
        next.setHours(base.getHours(), base.getMinutes(), base.getSeconds(), 0);
        onChange(next);
    };

    const handleTimeField = (field, rawVal) => {
        const cleaned = rawVal.replace(/\D/g, '');
        if (field === 'hour' && (parseInt(cleaned, 10) > 23 || cleaned.length > 2)) return;
        if (field === 'minute' && (parseInt(cleaned, 10) > 59 || cleaned.length > 2)) return;
        if (field === 'second' && (parseInt(cleaned, 10) > 59 || cleaned.length > 2)) return;

        if (cleaned.length !== 2) return; // only apply when full value typed

        const next = new Date(dateObj || new Date());
        if (field === 'hour') {
            next.setHours(parseInt(cleaned, 10));
            minuteRef.current?.focus();
        }
        if (field === 'minute') {
            next.setMinutes(parseInt(cleaned, 10));
            secondRef.current?.focus();
        }
        if (field === 'second') {
            next.setSeconds(parseInt(cleaned, 10));
        }
        onChange(next);
    };

    const handleClick = (e) => {
        setAnchorEl(anchorEl ? null : e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div className="est-datetime-input-wrapper">
            <button
                type="button"
                className="est-input est-datetime-btn"
                onClick={handleClick}
                aria-describedby={popperId}
            >
                <span className="est-datetime-text">
                    {dateObj ? formatDate(dateObj) : 'Select date & time'}
                </span>
                <img src={Images.calendarToday} alt="calendar" className="est-datetime-cal-icon" />
            </button>

            <Popper
                id={popperId}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                className="est-datetime-popper"
                style={{ position: 'absolute', zIndex: 1400 }}
            >
                <div className="est-datetime-dropdown">
                    <Calendar
                        date={dateObj || undefined}
                        onChange={handleCalendarChange}
                        locale={customLocale}
                        minDate={calendarMinDate}
                        maxDate={calendarMaxDate}
                    />

                    <div className="est-time-row">
                        <span className="est-time-label">Time</span>
                        <div className="est-time-inputs">
                            <input
                                ref={hourRef}
                                className="est-time-field"
                                type="text"
                                placeholder="HH"
                                maxLength={2}
                                defaultValue={hour}
                                key={`h-${hour}`}
                                onChange={(e) => handleTimeField('hour', e.target.value)}
                            />
                            <span className="est-time-sep">:</span>
                            <input
                                ref={minuteRef}
                                className="est-time-field"
                                type="text"
                                placeholder="mm"
                                maxLength={2}
                                defaultValue={minute}
                                key={`m-${minute}`}
                                onChange={(e) => handleTimeField('minute', e.target.value)}
                            />
                            <span className="est-time-sep">:</span>
                            <input
                                ref={secondRef}
                                className="est-time-field"
                                type="text"
                                placeholder="ss"
                                maxLength={2}
                                defaultValue={second}
                                key={`s-${second}`}
                                onChange={(e) => handleTimeField('second', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </Popper>

            {open && <div className="beck-drop" onClick={handleClose} />}
        </div>
    );
};

export const SegmentRow = ({ segments, idx, now, onUpdateBoundary, onAddPause, onRemovePause }) => {
    const seg = segments[idx];
    const isActive = seg.type === SEGMENT_TYPE.ACTIVE;
    const running = isRunningSegment(seg);
    const dur = Math.floor(segmentDurationMs(seg, now) / 1000);
    // Trailing pause can't be merged (no following ACTIVE to merge into) and removing it
    // would break the session.status === PAUSED invariant — so hide the action entirely.
    const canRemovePause = !isActive && idx < segments.length - 1;

    const startBounds = startPickerBounds(segments, idx);
    const endBounds = running ? null : endPickerBounds(segments, idx);

    return (
        <div className={`est-segment ${isActive ? 'est-segment-active' : 'est-segment-paused'}`}>
            <div className="est-segment-header">
                <span className="est-segment-title">{isActive ? 'Active Period' : 'Paused Period'}</span>
                <span className={`est-segment-duration${running ? ' est-segment-duration-running' : ''}`}>
                    {running && <span className="est-pulse-dot" />}
                    {formatSeconds(dur)}
                </span>
            </div>

            <div className="est-segment-row">
                <DateTimeInput
                    value={seg.start}
                    onChange={(d) => onUpdateBoundary(idx, d)}
                    minDate={startBounds.min}
                    maxDate={startBounds.max}
                />
                <ArrowForward className="est-arrow" />
                {running ? (
                    <div className="est-input est-input-running">Currently running…</div>
                ) : (
                    <DateTimeInput
                        value={seg.end}
                        onChange={(d) => onUpdateBoundary(idx + 1, d)}
                        minDate={endBounds.min}
                        maxDate={endBounds.max}
                    />
                )}
            </div>

            <div className="est-segment-action">
                {isActive ? (
                    <button type="button" className="est-link-btn est-link-add" onClick={() => onAddPause(idx)}>
                        <img src={Images.addBluePlus} alt="addIcon" />
                        Add Pause Here
                    </button>
                ) : canRemovePause ? (
                    <button type="button" className="est-link-btn est-link-remove" onClick={() => onRemovePause(idx)}>
                        <img src={Images.redTrashLine} alt="addIcon" />
                        Remove Pause
                    </button>
                ) : null}
            </div>
        </div>
    );
};
